import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { buildCourseFeeOverrideMap, getCourseCatalogDefaultFee } from '../utils/courseCatalogFee';

function errorText(err: { message?: string; details?: string; hint?: string } | null): string {
  if (!err) return '';
  return [err.message, err.details, err.hint].filter(Boolean).join(' ');
}

/** Postgres undefined column (42703) or PostgREST unknown column in request (PGRST204). */
function isMissingColumn(
  err: { code?: string | number; message?: string; details?: string; hint?: string } | null,
  col: string
): boolean {
  if (!err) return false;
  const msg = errorText(err);
  if (!msg.includes(col)) return false;
  const code = String(err.code ?? '');
  return code === '42703' || code === 'PGRST204';
}

function isSchemaCacheOrMissingCol(err: { code?: string | number } | null): boolean {
  const c = String(err?.code ?? '');
  return c === 'PGRST204' || c === '42703';
}

/**
 * Some projects have minimal `courses` rows (name, is_active only) or no tenant_id in the API cache.
 * `select('*')` can 400 when PostgREST's resolved column set does not match the live table.
 */
async function queryCoursesRowsImpl(): Promise<Record<string, unknown>[]> {
  if (!isSupabaseConfigured()) return [];

  const attempts = [
    () => supabase.from('courses').select('*').order('name'),
    () =>
      supabase
        .from('courses')
        .select('id,name,base_price,vat_rate,validity_months,is_active,description')
        .order('name'),
    () => supabase.from('courses').select('id,name,is_active').order('name'),
    () => supabase.from('courses').select('name').order('name'),
  ];

  for (const run of attempts) {
    const res = await run();
    if (!res.error) return (res.data ?? []) as Record<string, unknown>[];
    if (!isSchemaCacheOrMissingCol(res.error)) {
      console.warn('[coursesService] queryCoursesRowsImpl', res.error);
      return [];
    }
  }
  return [];
}

/** One or zero rows matching course name (column list fallbacks for older schemas). */
async function queryCourseRowsByNameImpl(name: string): Promise<Record<string, unknown>[]> {
  const all = await queryAllCourseRowsByNameImpl(name);
  return all.length > 0 ? [all[0]] : [];
}

/** All rows for a course name (case-insensitive) — handles duplicate catalog sync rows. */
async function queryAllCourseRowsByNameImpl(name: string): Promise<Record<string, unknown>[]> {
  const trimmed = name.trim();
  if (!trimmed) return [];
  const attempts = [
    () => supabase.from('courses').select('*').ilike('name', trimmed),
    () =>
      supabase
        .from('courses')
        .select('id,name,base_price,vat_rate,validity_months,is_active,description')
        .ilike('name', trimmed),
    () => supabase.from('courses').select('id,name,is_active').ilike('name', trimmed),
    () => supabase.from('courses').select('id,name').ilike('name', trimmed),
  ];

  for (const run of attempts) {
    const res = await run();
    if (!res.error) return (res.data ?? []) as Record<string, unknown>[];
    if (!isSchemaCacheOrMissingCol(res.error)) {
      throw res.error;
    }
  }
  return [];
}

function dedupeCourseRowsByName<T extends DbCourseRow>(rows: T[]): T[] {
  const byKey = new Map<string, T>();
  for (const row of rows) {
    const name = String(row.name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }
    const existingPrice = Number(existing.base_price || 0);
    const rowPrice = Number(row.base_price || 0);
    if (rowPrice > existingPrice) byKey.set(key, row);
  }
  return Array.from(byKey.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/**
 * PostgREST: "Could not find the 'base_price' column of 'courses' in the schema cache"
 */
function parseUnknownColumnFromPostgrest(err: { message?: string; details?: string } | null): string | null {
  const msg = errorText(err);
  const m = msg.match(/'([^']+)'\s+column of\s+'courses'/i);
  if (m?.[1]) return m[1];
  const m2 = msg.match(/"([^"]+)"\s+column of\s+"courses"/i);
  if (m2?.[1]) return m2[1];
  const m3 = msg.match(/'([^']+)'\s+column/i);
  return m3?.[1] ?? null;
}

export type DbCourseRow = {
  id: string;
  name: string;
  base_price?: number | null;
  validity_months?: number | null;
  description?: string | null;
  is_active?: boolean | null;
};

/**
 * When `courses.tenant_id` exists, keep only default-tenant rows. If the column is absent, all rows count.
 * Drops rows with `is_active === false` when that column is present.
 */
export function filterCoursesForDefaultTenant<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.filter((r) => {
    const tid = r.tenant_id;
    if (typeof tid === 'string' && tid !== DEFAULT_TENANT_ID) return false;
    if (r.is_active === false) return false;
    return true;
  });
}

/**
 * Active courses from the `courses` table (for quotes / products). No manual SQL — used by admin UI.
 * Does not filter by `tenant_id` in SQL (older DBs have no column); filters in JS instead.
 */
export async function fetchCoursesFromDb(): Promise<DbCourseRow[]> {
  const rows = await queryCoursesRowsImpl();
  return dedupeCourseRowsByName(filterCoursesForDefaultTenant(rows as DbCourseRow[]));
}

export type DbCourseLanguagePriceRow = {
  id: string;
  course_name: string;
  language: string;
  base_price: number;
};

/** Active course names from `courses` — use for admin lists and session dropdowns (not hardcoded COURSE_CONFIG). */
export async function fetchActiveCourseNamesFromDb(): Promise<string[]> {
  const rows = await fetchCoursesFromDb();
  return rows
    .map((r) => String(r.name || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

/** Rank course names so "sa" / "safe" prefer "Safe Pass" over "...Safety...". */
function rankCourseNameMatch(name: string, query: string): number {
  const n = name.toLowerCase().trim();
  const q = query.toLowerCase().trim();
  if (!q) return 99;
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  // Any word in the title starts with the query (e.g. "Fire Safety" for "sa")
  const wordStart = new RegExp(`(?:^|[\\s&/\\-_,.()]+)${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
  if (wordStart.test(name)) return 2;
  // Multi-word: every token is a prefix of some word ("safe pass" → Safe Pass)
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const words = n.split(/[\s&/\-_,.()]+/).filter(Boolean);
    if (tokens.every((t) => words.some((w) => w.startsWith(t)))) return 3;
  }
  if (n.includes(q)) return 4;
  return 99;
}

/** Search active course names by typed query (for new-session picker). */
export async function searchActiveCourseNamesFromDb(query: string, limit = 25): Promise<string[]> {
  const q = query.trim();
  if (!q || !isSupabaseConfigured()) return [];

  const pattern = `%${q.replace(/[%_]/g, '')}%`;
  // Fetch a wider pool, then rank so "sa"/"safe" puts Safe Pass above "...Safety...".
  const fetchLimit = Math.max(limit * 4, 80);
  const attempts = [
    () =>
      supabase
        .from('courses')
        .select('name,is_active')
        .ilike('name', pattern)
        .order('name')
        .limit(fetchLimit),
    () =>
      supabase
        .from('courses')
        .select('name')
        .ilike('name', pattern)
        .order('name')
        .limit(fetchLimit),
  ];

  for (const run of attempts) {
    const res = await run();
    if (res.error) {
      if (!isSchemaCacheOrMissingCol(res.error)) {
        console.warn('[coursesService] searchActiveCourseNamesFromDb', res.error);
        return [];
      }
      continue;
    }
    const names = [
      ...new Set(
        ((res.data || []) as Array<{ name?: string; is_active?: boolean | null }>)
          .filter((r) => r.is_active !== false)
          .map((r) => String(r.name || '').trim())
          .filter(Boolean)
      ),
    ]
      .map((name) => ({ name, rank: rankCourseNameMatch(name, q) }))
      .filter((r) => r.rank < 99)
      .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
      .slice(0, limit)
      .map((r) => r.name);
    return names;
  }
  return [];
}

/** All per-language prices (for fee lookup maps and admin UI). */
export async function fetchCourseLanguagePricesFromDb(): Promise<DbCourseLanguagePriceRow[]> {
  if (!isSupabaseConfigured()) return [];
  const { data, error } = await supabase
    .from('course_language_prices')
    .select('id, course_name, language, base_price')
    .order('course_name', { ascending: true })
    .order('language', { ascending: true });
  if (error) {
    if (isSchemaCacheOrMissingCol(error)) return [];
    throw error;
  }
  return (data || []).map((row: { id: string; course_name: string; language: string; base_price?: number }) => ({
    id: row.id,
    course_name: String(row.course_name || '').trim(),
    language: String(row.language || '').trim(),
    base_price: Number(row.base_price || 0),
  }));
}

export async function fetchCourseLanguagePricesForCourse(courseName: string): Promise<DbCourseLanguagePriceRow[]> {
  const trimmed = courseName.trim();
  if (!trimmed || !isSupabaseConfigured()) return [];
  const { data, error } = await supabase
    .from('course_language_prices')
    .select('id, course_name, language, base_price')
    .eq('course_name', trimmed)
    .order('language', { ascending: true });
  if (error) {
    if (isSchemaCacheOrMissingCol(error)) return [];
    throw error;
  }
  return (data || []).map((row: { id: string; course_name: string; language: string; base_price?: number }) => ({
    id: row.id,
    course_name: String(row.course_name || '').trim(),
    language: String(row.language || '').trim(),
    base_price: Number(row.base_price || 0),
  }));
}

/** Resolve default fee for one course (+ optional session language). */
export async function resolveCourseDefaultFeeFromDb(
  courseName: string,
  language?: string | null
): Promise<number> {
  const trimmed = courseName.trim();
  if (!trimmed || !isSupabaseConfigured()) {
    return getCourseCatalogDefaultFee(courseName, null, language);
  }
  const map: Record<string, number> = {};
  const { data: courseRow } = await supabase
    .from('courses')
    .select('base_price')
    .eq('name', trimmed)
    .maybeSingle();
  const base = Number((courseRow as { base_price?: number } | null)?.base_price);
  if (Number.isFinite(base) && base > 0) map[trimmed] = base;
  const lang = String(language || '').trim();
  if (lang) {
    const { data: langRows, error } = await supabase
      .from('course_language_prices')
      .select('language, base_price')
      .eq('course_name', trimmed);
    if (!error) {
      (langRows || []).forEach((row: { language?: string; base_price?: number }) => {
        const rowLang = String(row.language || '').trim();
        const price = Number(row.base_price);
        if (rowLang && Number.isFinite(price) && price > 0) {
          map[`${trimmed}|${rowLang}`] = price;
        }
      });
    }
  }
  return getCourseCatalogDefaultFee(trimmed, map, language);
}

/** Course base prices + `courseName|language` keys for session fee defaults. */
export async function fetchCourseFeeOverrideMap(): Promise<Record<string, number>> {
  const [courses, languagePrices] = await Promise.all([
    fetchCoursesFromDb(),
    fetchCourseLanguagePricesFromDb().catch(() => [] as DbCourseLanguagePriceRow[]),
  ]);
  return buildCourseFeeOverrideMap(courses, languagePrices);
}

export async function upsertCourseLanguagePrice(args: {
  courseName: string;
  language: string;
  basePrice: number;
}): Promise<void> {
  if (!isSupabaseConfigured()) throw new Error('Database is not configured');
  const course_name = args.courseName.trim();
  const language = args.language.trim();
  if (!course_name || !language) throw new Error('Course name and language are required');
  const base_price = Math.max(0, Number(args.basePrice) || 0);
  const { error } = await supabase.from('course_language_prices').upsert(
    {
      course_name,
      language,
      base_price,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'course_name,language' }
  );
  if (error) throw error;
}

export async function deleteCourseLanguagePrice(courseName: string, language: string): Promise<void> {
  if (!isSupabaseConfigured()) return;
  const course_name = courseName.trim();
  const languageTrimmed = language.trim();
  if (!course_name || !languageTrimmed) return;
  const { error } = await supabase
    .from('course_language_prices')
    .delete()
    .eq('course_name', course_name)
    .eq('language', languageTrimmed);
  if (error) throw error;
}

export async function syncCourseLanguagePrices(
  courseName: string,
  entries: Array<{ language: string; basePrice: number | null }>
): Promise<void> {
  const trimmed = courseName.trim();
  if (!trimmed) return;
  for (const entry of entries) {
    const lang = entry.language.trim();
    if (!lang) continue;
    const price = entry.basePrice;
    if (price == null || Number.isNaN(Number(price)) || Number(price) <= 0) {
      await deleteCourseLanguagePrice(trimmed, lang);
    } else {
      await upsertCourseLanguagePrice({ courseName: trimmed, language: lang, basePrice: Number(price) });
    }
  }
}

function buildDesiredCourseInsert(params: {
  name: string;
  base_price?: number;
  vat_rate?: number;
  validity_months?: number | null;
  description?: string | null;
}): Record<string, unknown> {
  const name = params.name.trim();
  const row: Record<string, unknown> = {
    name,
    base_price: params.base_price ?? 0,
    vat_rate: params.vat_rate ?? 23,
    validity_months: params.validity_months ?? 36,
    is_active: true,
    tenant_id: DEFAULT_TENANT_ID,
  };
  if (params.description != null && params.description !== '') {
    row.description = params.description;
  }
  return row;
}

/** Keep only keys that exist on a sample row from `courses` — avoids PGRST204 on INSERT. */
function pickInsertKeysMatchingSample(
  desired: Record<string, unknown>,
  sampleKeys: Set<string>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(desired)) {
    if (!sampleKeys.has(k)) continue;
    if (v === undefined) continue;
    if (k === 'description' && (v === '' || v == null)) continue;
    out[k] = v;
  }
  // name must always be present
  out.name = desired.name;
  return out;
}

/**
 * Create a quote/product course row — admins do this from Course Management (no Supabase SQL editor).
 * Uses column keys from an existing row when possible (single clean INSERT). Empty table: try small payloads first
 * so PostgREST does not log spurious PGRST204s for tenant_id/vat_rate/etc.
 */
export async function createCourseProduct(params: {
  name: string;
  base_price?: number;
  vat_rate?: number;
  validity_months?: number | null;
  description?: string | null;
}): Promise<void> {
  if (!isSupabaseConfigured()) throw new Error('Database is not configured');

  const desired = buildDesiredCourseInsert(params);
  const name = desired.name as string;
  if (!name || typeof name !== 'string') throw new Error('Course name is required');

  const existing = await queryAllCourseRowsByNameImpl(name);
  if (existing.length > 0) {
    throw new Error('A course with this name already exists in the database.');
  }

  const samples = await queryCoursesRowsImpl();
  if (samples.length > 0) {
    const sampleKeys = new Set(Object.keys(samples[0]));
    const payload = pickInsertKeysMatchingSample(desired, sampleKeys);
    const { error } = await supabase.from('courses').insert([payload]);
    if (!error) return;
    if (String(error.code) === '23505') {
      throw new Error('A course with this name already exists in the database.');
    }
    throw error;
  }

  const vm = params.validity_months ?? 36;
  const bp = params.base_price ?? 0;
  const vr = params.vat_rate ?? 23;

  const emptyTableAttempts: Record<string, unknown>[] = [
    { name, is_active: true },
    { name },
    { name, validity_months: vm, is_active: true },
    { name, base_price: bp, validity_months: vm, is_active: true },
    { name, base_price: bp, vat_rate: vr, validity_months: vm, is_active: true },
    { ...desired },
  ];

  let lastError: { code?: string; message?: string } | null = null;

  for (const payload of emptyTableAttempts) {
    const { error } = await supabase.from('courses').insert([payload]);
    if (!error) return;
    lastError = error;
    if (String(error.code) === '23505') {
      throw new Error('A course with this name already exists in the database.');
    }
    if (!isSchemaCacheOrMissingCol(error)) {
      throw error;
    }
  }

  const attemptRow = { ...desired };
  for (let i = 0; i < 16; i++) {
    const { error } = await supabase.from('courses').insert([attemptRow]);
    if (!error) return;
    lastError = error;
    if (String(error.code) === '23505') {
      throw new Error('A course with this name already exists in the database.');
    }
    if (!isSchemaCacheOrMissingCol(error)) {
      throw error;
    }
    const unknownCol = parseUnknownColumnFromPostgrest(error);
    if (unknownCol && Object.prototype.hasOwnProperty.call(attemptRow, unknownCol)) {
      delete attemptRow[unknownCol];
      continue;
    }
    break;
  }

  if (lastError) throw lastError;
}

/**
 * Save course fee / validity to `courses` for Super Admin edits. Updates an existing row by name;
 * inserts a product row if the course exists only in COURSE_CONFIG.
 * Does not filter by tenant_id in SQL (some DBs have no `courses.tenant_id` column).
 */
export async function upsertCourseProductPricing(params: {
  name: string;
  base_price: number;
  validity_months?: number | null;
}): Promise<void> {
  if (!isSupabaseConfigured()) throw new Error('Database is not configured');

  const name = params.name.trim();
  if (!name) throw new Error('Course name is required');

  const basePrice = Math.max(0, Number(params.base_price) || 0);
  const validityMonths = params.validity_months ?? 36;

  const rows = await queryCourseRowsByNameImpl(name);

  const row = rows && rows.length > 0 ? (rows[0] as Record<string, unknown> & { id: string }) : null;
  if (row?.id) {
    // Only PATCH columns that exist on this table — `*` omits missing columns, so no PGRST204 on probe.
    const toSend: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(row, 'base_price')) toSend.base_price = basePrice;
    if (Object.prototype.hasOwnProperty.call(row, 'validity_months')) toSend.validity_months = validityMonths;

    if (Object.keys(toSend).length === 0) {
      return;
    }

    const { error } = await supabase.from('courses').update(toSend).eq('id', row.id);
    if (!error) return;

    const unknownCol = parseUnknownColumnFromPostgrest(error);
    if (unknownCol && Object.prototype.hasOwnProperty.call(toSend, unknownCol)) {
      delete toSend[unknownCol];
      if (Object.keys(toSend).length === 0) return;
      const { error: err2 } = await supabase.from('courses').update(toSend).eq('id', row.id);
      if (err2) throw err2;
      return;
    }
    if (isMissingColumn(error, 'base_price') && 'base_price' in toSend) {
      delete toSend.base_price;
      if (Object.keys(toSend).length === 0) return;
      const { error: err2 } = await supabase.from('courses').update(toSend).eq('id', row.id);
      if (err2) throw err2;
      return;
    }
    if (isMissingColumn(error, 'validity_months') && 'validity_months' in toSend) {
      delete toSend.validity_months;
      if (Object.keys(toSend).length === 0) return;
      const { error: err2 } = await supabase.from('courses').update(toSend).eq('id', row.id);
      if (err2) throw err2;
      return;
    }
    throw error;
  }

  await createCourseProduct({
    name,
    base_price: basePrice,
    validity_months: validityMonths,
  });
}

/**
 * Rename an existing course product row by course name.
 * Keeps historical references intact when they point by row id; if references are by plain text name,
 * callers should ensure downstream behavior is acceptable before renaming.
 */
export async function renameCourseProduct(oldName: string, newName: string): Promise<void> {
  if (!isSupabaseConfigured()) throw new Error('Database is not configured');
  const fromName = oldName.trim();
  const toName = newName.trim();
  if (!fromName || !toName) throw new Error('Course name is required');
  if (fromName === toName) return;

  const existingTargetRows = await queryCourseRowsByNameImpl(toName);
  if (existingTargetRows.length > 0) {
    throw new Error('A course with this name already exists in the database.');
  }

  const rows = await queryCourseRowsByNameImpl(fromName);
  const row = rows && rows.length > 0 ? (rows[0] as Record<string, unknown> & { id: string }) : null;
  if (!row?.id) {
    throw new Error('Could not rename this course because its database row was not found.');
  }

  const patch: Record<string, unknown> = { name: toName, updated_at: new Date().toISOString() };
  let { error } = await supabase.from('courses').update(patch).eq('id', row.id);
  if (error && isMissingColumn(error, 'updated_at')) {
    delete patch.updated_at;
    ({ error } = await supabase.from('courses').update(patch).eq('id', row.id));
  }
  if (error) {
    if (String(error.code ?? '') === '23505') {
      throw new Error('A course with this name already exists in the database.');
    }
    throw error;
  }

  try {
    await supabase
      .from('course_language_prices')
      .update({ course_name: toName, updated_at: new Date().toISOString() })
      .eq('course_name', fromName);
  } catch {
    /* language price table may not exist on older DBs */
  }
}

/** Permanently removes every `courses` row with this name (case-insensitive). */
export async function deleteCourseProductByName(name: string): Promise<{ deletedCount: number }> {
  if (!isSupabaseConfigured()) return { deletedCount: 0 };

  const trimmed = name.trim();
  if (!trimmed) throw new Error('Course name is required');

  const rows = await queryAllCourseRowsByNameImpl(trimmed);
  if (rows.length === 0) {
    throw new Error('No course found with this name in the database.');
  }

  try {
    await supabase.from('course_language_prices').delete().ilike('course_name', trimmed);
  } catch {
    /* optional table */
  }

  const { data, error } = await supabase.from('courses').delete().ilike('name', trimmed).select('id');
  if (error) {
    if (String(error.code ?? '') === '23503') {
      throw new Error(
        'This course is still referenced elsewhere (for example quote line items). Remove those references first, then delete again.'
      );
    }
    throw error;
  }

  const deletedCount = (data || []).length;
  if (deletedCount === 0) {
    throw new Error('Delete did not remove any rows. Check database permissions and try again.');
  }

  return { deletedCount };
}

/**
 * Remove duplicate course rows, keeping one row per course name (highest base_price wins).
 * Safe to run repeatedly; does not touch `course_language_prices`.
 */
export async function dedupeCourseRowsInDb(): Promise<{ before: number; after: number; removed: number }> {
  if (!isSupabaseConfigured()) return { before: 0, after: 0, removed: 0 };

  const all = await queryCoursesRowsImpl();
  const before = all.length;
  if (before <= 1) return { before, after: before, removed: 0 };

  const byKey = new Map<string, Record<string, unknown>>();
  for (const row of all) {
    const name = String((row as { name?: string }).name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }
    const existingPrice = Number((existing as { base_price?: number }).base_price || 0);
    const rowPrice = Number((row as { base_price?: number }).base_price || 0);
    if (rowPrice > existingPrice) byKey.set(key, row);
  }

  const keepIds = new Set(
    [...byKey.values()].map((r) => String((r as { id?: string }).id || '')).filter(Boolean)
  );
  const deleteIds = all
    .map((r) => String((r as { id?: string }).id || ''))
    .filter((id) => id && !keepIds.has(id));

  const CHUNK = 100;
  for (let i = 0; i < deleteIds.length; i += CHUNK) {
    const slice = deleteIds.slice(i, i + CHUNK);
    const { error } = await supabase.from('courses').delete().in('id', slice);
    if (error) throw error;
  }

  const after = keepIds.size;
  return { before, after, removed: before - after };
}

/** Used by `coursesCatalogSync`, `quotesService`, etc. */
export const queryCoursesRows = queryCoursesRowsImpl;
export const queryCourseRowsByName = queryCourseRowsByNameImpl;
